import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MAX_PAGE_SIZE, PAGE_SIZE_OPTIONS } from './lmApi';
import { BATCH_STATUSES } from './lmColumns';

/**
 * List Management query state lives in the URL (#770 LM-02), so a refresh or
 * Back restores the same tab, source, filters, sort and page.
 *
 * Server-owned: page, size, sort, order, search and every filter (all are
 * sent to /api/list-management). Local-only presentation preferences (column
 * visibility, collapsed batches) are NOT here — they live in localStorage.
 */
export const TABS = ['imports', 'staging', 'pool', 'history', 'deleted'];
export const SOURCES = ['all', 'csv', 'pitchbook', 'legacy_staging'];
export const POOL_SUBS = ['contacts', 'distribution', 'balance'];
const FILTER_KEYS = ['entity', 'match', 'validation', 'owner', 'imp', 'bstatus', 'migration'];
/** An owner-operation id, as the migration bell links to it (#862). */
const OPERATION_ID = /^[A-Za-z0-9_-]{1,80}$/;
const SORT_DIRS = ['asc', 'desc'];

export const TAB_VIEW = { imports: 'imports', staging: 'staging', pool: 'pool', deleted: 'deleted' };

function pick(value, allowed, fallback) {
    return allowed.includes(value) ? value : fallback;
}

export function parseLmQuery(params) {
    const get = (k) => params.get(k) || '';
    const size = Number(get('size')) || PAGE_SIZE_OPTIONS[0];
    return {
        tab: pick(get('tab'), TABS, 'imports'),
        source: pick(get('source'), SOURCES, 'all'),
        sub: pick(get('sub'), POOL_SUBS, 'contacts'),
        q: get('q').slice(0, 100),
        page: Math.max(1, Number(get('page')) || 1),
        // Only offered sizes: a hand-typed ?size=10 would fetch 10 while the picker shows 25.
        size: PAGE_SIZE_OPTIONS.includes(size) ? size : PAGE_SIZE_OPTIONS[0],
        sort: get('sort'),
        order: pick(get('order'), SORT_DIRS, ''),
        entity: pick(get('entity'), ['Company', 'Contact'], ''),
        match: get('match'),
        validation: pick(get('validation'), ['valid', 'needs_review'], ''),
        owner: get('owner'),
        imp: get('imp'),
        // Import History's batch-status filter (#775 QA).
        bstatus: pick(get('bstatus'), BATCH_STATUSES, ''),
        // The one-time owner migration whose panel Import History opens (#862).
        migration: OPERATION_ID.test(get('migration')) ? get('migration') : '',
    };
}

/** API params for GET /rows from the URL state. Never sends page_size > 100. */
export function rowParams(state) {
    const params = {
        view: TAB_VIEW[state.tab],
        page: state.page,
        page_size: Math.min(MAX_PAGE_SIZE, state.size),
    };
    if (state.q) params.search = state.q;
    if (state.sort) params.sort = state.sort;
    if (state.order) params.order = state.order;
    if (state.source !== 'all') params.source = state.source;
    if (state.entity) params.entity_type = state.entity;
    if (state.match) params.match_status = state.match;
    if (state.validation) params.validation_status = state.validation;
    if (state.owner) params.owner = state.owner;
    if (state.imp) params.import_id = state.imp;
    return params;
}

/** The selection context: changing any of these clears the selection. */
export function selectionContextKey(state) {
    return [state.tab, state.source, state.q, state.entity, state.match, state.validation, state.owner, state.imp].join('|');
}

export default function useLmQueryState() {
    const [params, setParams] = useSearchParams();
    const state = useMemo(() => parseLmQuery(params), [params]);

    const update = useCallback((patch, { resetPage = true } = {}) => {
        const next = new URLSearchParams(params);
        const merged = { ...patch };
        if (resetPage && !('page' in patch)) merged.page = 1;
        Object.entries(merged).forEach(([k, v]) => {
            const empty = v === '' || v == null || (k === 'page' && Number(v) === 1)
                || (k === 'source' && v === 'all') || (k === 'tab' && v === 'imports')
                || (k === 'sub' && v === 'contacts') || (k === 'size' && Number(v) === PAGE_SIZE_OPTIONS[0]);
            if (empty) next.delete(k);
            else next.set(k, String(v));
        });
        setParams(next, { replace: false });
    }, [params, setParams]);

    const setTab = useCallback((tab) => {
        const patch = { tab, q: '', sort: '', order: '', imp: '' };
        FILTER_KEYS.forEach((k) => { patch[k] = ''; });
        update(patch);
    }, [update]);

    return { state, update, setTab };
}
